import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Col, Row } from 'react-bootstrap';
import { RenderIf } from '..';
import ChatParticipantsList from './participantsList';
import ChatWindow from './window';
import theme from '../../theme';
import { getAge, getCurrentTimestamp } from './helpers';
import { useChatRoomConversation, useCurrentParticipant, useReceiverProfile } from './hooks';
import { updateChatRoom, updateUnreadMessages } from '../../store/actions/chat-rooms';
import {
    StyledChatContainer,
    StyledChatForm,
    StyledChatFormSubmitBtn,
    StyledChatFormBtnIcon,
    StyledChatSidebar,
    StyledChatTitle,
    StyledContainer,
    StyledSidebarAvatar,
    StyledSidebarInfoWrapper,
    StyledChatInfo,
} from './styled';
import SimpleModal from '../simple-modal';

function Chat({
    chatParticipants,
    chatRoom,
    isIndividualChat,
    chatTitleLabel,
    chatTitle,
    chatDescription,
    facilitator,
    className,
    onParticipantClick,
}) {
    const dispatch = useDispatch();
    const [messageInput, setMessageInput] = useState('');
    const [showParticipantModal, setShowParticipantModal] = useState(false);
    const chatRoomConversation = useChatRoomConversation(chatRoom);
    const formRef = useRef();

    const currentParticipant = useCurrentParticipant(chatParticipants);
    let { name, image, dateOfBirth } = currentParticipant;
    if (facilitator) {
        name = facilitator.name;
        image = facilitator.image;
        dateOfBirth = facilitator.dateOfBirth;
    }
    const profileId = useSelector(state => state.auth.profile.id);
    const dateOfBirthTimestamp = dateOfBirth && dateOfBirth.seconds ? dateOfBirth.seconds : undefined;

    const receiverProfile = useReceiverProfile(currentParticipant, chatRoom, chatParticipants, isIndividualChat);

    const handleOnSendNewMessage = e => {
        e.preventDefault();

        const message = messageInput.trim();

        if (chatRoom && chatRoom.id && message) {
            const currentTimestamp = getCurrentTimestamp();

            const newMessage = {
                messages: [
                    {
                        date: currentTimestamp,
                        message,
                    },
                ],
                updatedAt: currentTimestamp,
                profileId,
            };

            const newConversation = [...chatRoomConversation, newMessage];

            setMessageInput('');
            formRef.current.reset();

            dispatch(
                updateChatRoom({
                    docId: chatRoom.id,
                    data: { conversation: newConversation },
                }),
            );

            if (isIndividualChat) {
                dispatch(
                    updateUnreadMessages({ chatRoom, profileId: receiverProfile.id || currentParticipant.id, incrementBy: 1 }),
                );
            }
        }
    };

    const handleOnChangeMessage = e => {
        if (chatRoom) {
            setMessageInput(e.target.value);
        } else {
            setMessageInput('');
        }
    };

    const handleOnKeyDownMessage = e => {
        const shouldSubmit = e.keyCode === 13 && e.shiftKey === false;

        if (shouldSubmit) {
            e.preventDefault();
            formRef.current.dispatchEvent(new Event('submit'));
        }
    };

    const handleViewParticipantsClick = e => {
        e.preventDefault();
        setShowParticipantModal(true);
    };

    return (
        <StyledContainer className={className}>
            <div className="d-flex flex-column flex-lg-row">
                {/* chat sidebar */}
                <StyledChatSidebar>
                    <StyledChatTitle className="p-4">
                        Sala de Chat
                        <RenderIf isTrue={!isIndividualChat}>
                            <StyledChatInfo>
                                <Button variant="link" size="sm" className="p-0" onClick={handleViewParticipantsClick}>
                                    Ver participantes
                                </Button>
                            </StyledChatInfo>
                        </RenderIf>
                    </StyledChatTitle>

                    <Row className="no-gutters">
                        {/* photo and description details */}
                        <Col className={`flex-grow-1 col-12 ${isIndividualChat && `col-lg-6`}`}>
                            <StyledSidebarInfoWrapper>
                                {/* account */}
                                <div className="d-flex flex-column align-items-center mb-3">
                                    {/* photo */}
                                    <StyledSidebarAvatar name={name} imgSrc={image} size="large" className="mb-3" />

                                    {/* name */}
                                    <div className="font-weight-600 mb-2">{name}</div>

                                    {/* age */}
                                    <RenderIf isTrue={!!dateOfBirthTimestamp}>
                                        <div>{getAge(dateOfBirthTimestamp)} años</div>
                                    </RenderIf>
                                </div>

                                {/* title and description */}
                                <div className="px-4">
                                    <RenderIf isTrue={!!chatTitleLabel && !!chatTitle}>
                                        <div className="mb-2">
                                            <b>{chatTitleLabel}: </b> <span>{chatTitle}</span>
                                        </div>
                                    </RenderIf>

                                    <RenderIf isTrue={!chatTitleLabel && !!chatTitle}>
                                        <div className="mb-2">
                                            <span>{chatTitle}</span>
                                        </div>
                                    </RenderIf>

                                    <RenderIf isTrue={!!chatDescription}>
                                        <div>{chatDescription}</div>
                                    </RenderIf>
                                </div>
                            </StyledSidebarInfoWrapper>
                        </Col>

                        {/* participants list */}
                        <RenderIf isTrue={isIndividualChat}>
                            <Col className="flex-grow-1 col-12 col-lg-6">
                                <ChatParticipantsList
                                    currentParticipant={currentParticipant}
                                    chatParticipants={chatParticipants}
                                    onParticipantClick={onParticipantClick}
                                />
                            </Col>
                        </RenderIf>
                    </Row>
                </StyledChatSidebar>

                {/* chat content */}
                <StyledChatContainer>
                    {/* chat window */}
                    <ChatWindow
                        currentParticipant={currentParticipant}
                        chatRoom={chatRoom}
                        chatParticipants={chatParticipants}
                        chatRoomConversation={chatRoomConversation}
                        isIndividualChat={isIndividualChat}
                    />
                    <RenderIf isTrue={!!chatRoom}>
                        {/* chat form */}
                        <StyledChatForm
                            ref={formRef}
                            className="d-flex justify-content-between align-items-center"
                            onSubmit={handleOnSendNewMessage}
                        >
                            {/* text input */}
                            <textarea
                                placeholder="Escribe..."
                                autoComplete="off"
                                value={messageInput}
                                onChange={handleOnChangeMessage}
                                onKeyDown={handleOnKeyDownMessage}
                            />

                            {/* chat submit */}
                            <div className="pr-3">
                                <StyledChatFormSubmitBtn type="submit">
                                    <StyledChatFormBtnIcon size="lg" colorCircle={theme.bootstrap.success} color="white" />
                                </StyledChatFormSubmitBtn>
                            </div>
                        </StyledChatForm>
                    </RenderIf>
                </StyledChatContainer>
                <SimpleModal
                    title="Participantes del Foro"
                    showIf={showParticipantModal}
                    onHide={() => setShowParticipantModal(false)}
                >
                    <ChatParticipantsList currentParticipant={currentParticipant} chatParticipants={chatParticipants} />
                </SimpleModal>
            </div>
        </StyledContainer>
    );
}

Chat.propTypes = {
    chatParticipants: PropTypes.array,
    chatRoom: PropTypes.any,
    isIndividualChat: PropTypes.bool,
    chatTitleLabel: PropTypes.string,
    chatTitle: PropTypes.string,
    chatDescription: PropTypes.string,
    facilitator: PropTypes.any,
    className: PropTypes.string,
    onParticipantClick: PropTypes.func,
};

Chat.defaultProps = {
    chatParticipants: undefined,
    chatRoom: undefined,
    isIndividualChat: false,
    chatTitleLabel: undefined,
    chatTitle: undefined,
    chatDescription: undefined,
    facilitator: undefined,
    className: '',
    onParticipantClick: () => {},
};

export default Chat;
